// Phase A.3 — SIKA chat-first page.
// Three-column layout: goals rail | conversation | context pane.
// Replaces the old static SikaPage with a conversational interface.

import { useEffect, useRef, useState } from "react";
import Markdown from "react-markdown";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useSikaGoals } from "../../sika/useSikaGoals.ts";
import { useSikaChat } from "../../sika/useSikaChat.ts";
import { useRolloverChain } from "../../vault/useRolloverChain.ts";
import { SikaChatInput } from "./SikaChatInput.tsx";
import { SikaChatMessage } from "./SikaChatMessage.tsx";
import { SikaGoalsRail } from "./SikaGoalsRail.tsx";
import { SikaHistoryRail } from "./SikaHistoryRail.tsx";
import { SikaContextPane } from "./SikaContextPane.tsx";
import { SikaOpeningCard } from "./SikaOpeningCard.tsx";
import { SikaAgentDeploy } from "./SikaAgentDeploy.tsx";
import { Icon } from "../../components/icons/Icon.tsx";
import "../../styles/sika.css";

export function SikaChatPageLegacy(): JSX.Element {
  const { user } = useAuthSession();
  const { goals, loading: goalsLoading, refetch: refetchGoals } = useSikaGoals();
  const { chain } = useRolloverChain();
  const chat = useSikaChat();
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevGoalIdRef = useRef<string | null>(null);
  const [mobileGoalsOpen, setMobileGoalsOpen] = useState(false);
  const [railTab, setRailTab] = useState<"history" | "goals">("history");

  const userInitial = user?.email?.charAt(0).toUpperCase() ?? "U";

  // Refresh goals rail when a new goal is created via chat
  useEffect(() => {
    if (chat.goalId && chat.goalId !== prevGoalIdRef.current) {
      prevGoalIdRef.current = chat.goalId;
      void refetchGoals();
    }
  }, [chat.goalId, refetchGoals]);

  // Auto-scroll only when user is near the bottom (within 200px) to avoid
  // jarring scroll jumps when cards render and push older messages up.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
    if (isNearBottom) el.scrollTop = el.scrollHeight;
  }, [chat.messages.length, chat.sending, chat.streamingText]);

  const visibleMessages = chat.messages.filter((m) => m.role !== "tool" || m.card_type != null);

  // §4.7: Returning users with active goals see the opening context card
  const activeGoals = goals.filter(
    (g) => g.status === "active" || g.status === "in_progress",
  );
  const hasActiveGoals = activeGoals.length > 0 && !goalsLoading;

  return (
    <div className="sika-shell">
      <div className="sika-rail-tabs-wrap">
        <div className="sika-rail-tabs">
          <button
            type="button"
            className={`sika-rail-tab ${railTab === "history" ? "active" : ""}`}
            onClick={() => setRailTab("history")}
          >
            <Icon name="clock" size={12} />
            History
          </button>
          <button
            type="button"
            className={`sika-rail-tab ${railTab === "goals" ? "active" : ""}`}
            onClick={() => setRailTab("goals")}
          >
            <Icon name="target" size={12} />
            Goals
          </button>
        </div>
        {railTab === "history" ? (
          <SikaHistoryRail
            activeSessionId={chat.sessionId}
            onSelectSession={chat.loadSession}
            onNewConversation={chat.newSession}
          />
        ) : (
          <SikaGoalsRail
            goals={goals}
            activeGoalId={chat.goalId}
            onSelectGoal={chat.setGoalId}
            onNewConversation={chat.newSession}
            loading={goalsLoading}
          />
        )}
      </div>

      {/* Mobile drawer */}
      {mobileGoalsOpen && (
        <div className="mobile-goals-overlay" onClick={() => setMobileGoalsOpen(false)}>
          <div className="mobile-goals-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="sika-rail-tabs" style={{ padding: "8px 12px" }}>
              <button
                type="button"
                className={`sika-rail-tab ${railTab === "history" ? "active" : ""}`}
                onClick={() => setRailTab("history")}
              >
                History
              </button>
              <button
                type="button"
                className={`sika-rail-tab ${railTab === "goals" ? "active" : ""}`}
                onClick={() => setRailTab("goals")}
              >
                Goals
              </button>
            </div>
            {railTab === "history" ? (
              <SikaHistoryRail
                activeSessionId={chat.sessionId}
                onSelectSession={(sid, gid) => { chat.loadSession(sid, gid); setMobileGoalsOpen(false); }}
                onNewConversation={() => { chat.newSession(); setMobileGoalsOpen(false); }}
              />
            ) : (
              <SikaGoalsRail
                goals={goals}
                activeGoalId={chat.goalId}
                onSelectGoal={(id) => { chat.setGoalId(id); setMobileGoalsOpen(false); }}
                onNewConversation={() => { chat.newSession(); setMobileGoalsOpen(false); }}
                loading={goalsLoading}
              />
            )}
          </div>
        </div>
      )}

      <div className="conv-pane">
        <div className="conv-head">
          <button
            type="button"
            className="mobile-goals-toggle"
            onClick={() => setMobileGoalsOpen(true)}
            aria-label="View goals"
          >
            <Icon name="menu" size={18} />
          </button>
          <div className="sika-mark">S</div>
          <div className="who">
            SIKA
            <div className="sub">Goal Intelligence Agent</div>
          </div>
        </div>

        <div className="conv-body" ref={scrollRef} role="log" aria-label="SIKA conversation">
          <div className="conv-inner">
            {chat.loadingHistory ? (
              <div className="msg sika">
                <div className="av">S</div>
                <div>
                  <div className="bubble">
                    <div className="typing">
                      <span className="dot" />
                      <span className="dot" />
                      <span className="dot" />
                    </div>
                  </div>
                </div>
              </div>
            ) : visibleMessages.length === 0 ? (
              <>
                {/* §4.7: Returning users see opening context card; new users see suggestion chips */}
                {hasActiveGoals ? (
                  <>
                    <SikaOpeningCard
                      goals={activeGoals}
                      chainDay={chain?.current_week ?? null}
                      chainStatus={
                        chain?.status === "active" ? "active"
                        : chain?.status === "bust" ? "reset"
                        : "none"
                      }
                    />
                    <div className="quick-replies">
                      <button
                        type="button"
                        className="qr primary"
                        onClick={() => void chat.send("Show me my chain status")}
                      >
                        View chain
                      </button>
                      <button
                        type="button"
                        className="qr"
                        onClick={() => void chat.send("Run a new scenario")}
                      >
                        Run scenario
                      </button>
                      <button
                        type="button"
                        className="qr"
                        onClick={() => void chat.send("I want to start a new goal")}
                      >
                        New goal
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="msg sika">
                      <div className="av">S</div>
                      <div>
                        <div className="bubble">What are you working toward?</div>
                      </div>
                    </div>
                    <div className="suggestion-grid">
                      <button
                        type="button"
                        className="qr"
                        onClick={() => void chat.send("I want to save for a MacBook Pro")}
                      >
                        <Icon name="star" size={14} />
                        <span>Save for a MacBook</span>
                      </button>
                      <button
                        type="button"
                        className="qr"
                        onClick={() => void chat.send("I want to build a house fund")}
                      >
                        <Icon name="home" size={14} />
                        <span>Build a house fund</span>
                      </button>
                      <button
                        type="button"
                        className="qr"
                        onClick={() => void chat.send("I want to plan a trip to Dubai")}
                      >
                        <Icon name="bolt" size={14} />
                        <span>Plan a trip to Dubai</span>
                      </button>
                      <button
                        type="button"
                        className="qr"
                        onClick={() => void chat.send("Help me save for school fees")}
                      >
                        <Icon name="knowledge" size={14} />
                        <span>Save for school fees</span>
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              visibleMessages.map((msg) => (
                <SikaChatMessage
                  key={msg.id}
                  msg={msg}
                  userInitial={userInitial}
                  onAction={(text) => void chat.send(text)}
                  sending={chat.sending}
                />
              ))
            )}

            {chat.sending && (
              <div className="msg sika">
                <div className="av">S</div>
                <div>
                  {/* Live agent deployment steps — shows tool execution progress */}
                  {chat.agentSteps.length > 0 && (
                    <SikaAgentDeploy steps={chat.agentSteps} variant="inline" />
                  )}
                  <div className="bubble">
                    {chat.streamingText ? (
                      <span><Markdown>{chat.streamingText}</Markdown><span className="stream-cursor" /></span>
                    ) : chat.agentSteps.length === 0 ? (
                      <div className="typing">
                        <span className="dot" />
                        <span className="dot" />
                        <span className="dot" />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            {chat.error && (
              <div className="msg sika error-msg">
                <div className="av">S</div>
                <div>
                  <div className="bubble">
                    Having trouble connecting. Give me a moment.
                  </div>
                  <button
                    type="button"
                    className="retry-btn"
                    onClick={() => {
                      const last = chat.messages.filter((m) => m.role === "user").pop();
                      if (last) void chat.send(last.content);
                    }}
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <SikaChatInput onSend={(text) => void chat.send(text)} disabled={chat.sending} />
      </div>

      <SikaContextPane goalId={chat.goalId} />
    </div>
  );
}
